import { useFormik } from "formik";
import { FC, useCallback, useContext, useEffect, useState } from "react";
import * as yup from "yup";
import {
  Alert,
  Box,
  Card,
  Checkbox,
  FormControlLabel,
  IconButton,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { AxiosError } from "axios";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useDebounce, useUnmount } from "react-use";

import { Dialog } from "components/Dialog";
import { DropZone } from "components/CustomUpload/ImageDropZone";
import { getCheckProjectNameExists } from "pages/Projects/getCheckProjectNameExists";

import { ProjectTypes } from "interfaces/project";
import { Dropdown } from "components/Dropdown";
import {
  CsvErrorFeedback,
  CsvErrorWrapper
} from "components/CaseManagement/CsvErrors/CsvErrorFeedback";
import { AxiosContext } from "contexts/AxiosContext";
import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";
import { FeaturesContext } from "contexts/FeaturesContext";
import { useCaseCreationTemplatesQuery } from "hooks/queries/useCaseCreationTemplatesQuery";
import { downloadFile } from "core/utils/aws/downloadFile";

const ERROR_MESSAGE_PROJECT_NAME_EXISTS =
  "A project with the same name already exists.";

export interface Props {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (values: FormValues) => void;
}

const NAME_MIN_LENGTH = 3;
const NAME_MAX_LENGTH = 500;

interface FormValues {
  enableGrouping: boolean;
  projectName: string;
  projectType: string;
}

const initialValues: FormValues = {
  enableGrouping: false,
  projectName: "",
  projectType: ""
};

type FormValuesType = typeof initialValues;

export const CreateMultipleCasesDialog: FC<Props> = ({
  isOpen,
  onClose
}): JSX.Element => {
  const { axios } = useContext(AxiosContext)!;
  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);
  const { hasFeature } = useContext(FeaturesContext);
  const [file, setFile] = useState<File | null>(null);
  const [debouncedProjectName, setDebouncedProjectName] = useState("");

  const {
    data: template,
    refetch: refetchTemplate
  } = useCaseCreationTemplatesQuery({
    enabled: isOpen
  });

  const postCreateProject = useMutation<
    any,
    AxiosError<CsvErrorWrapper>,
    FormData,
    unknown
  >({
    mutationFn: (formData: FormData) =>
      axios.post("/cases_bulk_create", formData).then(r => r.data)
  });

  const controller = new AbortController();
  useUnmount(() => {
    controller.abort();
  });

  const validationSchema = yup.object({
    projectName: yup.string().when("enableGrouping", {
      is: true,
      then: yup
        .string()
        .required("Project Name is required")
        .trim()
        .min(
          NAME_MIN_LENGTH,
          `Name must be at least ${NAME_MIN_LENGTH} characters`
        )
        .max(
          NAME_MAX_LENGTH,
          `Name must be fewer than ${NAME_MAX_LENGTH} characters`
        )
        .matches(
          /^[a-zA-Z0-9\-\s]+$/,
          "Only alphanumeric characters, dashes and spaces allowed"
        )
    }),
    projectType: yup.string().when("enableGrouping", {
      is: true,
      then: yup.string().required("Project type is required")
    })
  });

  const formik = useFormik<FormValues>({
    initialValues,
    validationSchema,
    validateOnMount: true,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: values => {
      handleSubmit(formik.isValid, values, () => {});
    }
  });

  const checkProjectExists = useQuery<boolean>({
    queryKey: ["project-name-exists", debouncedProjectName],
    queryFn: () => getCheckProjectNameExists(axios, debouncedProjectName),
    cacheTime: 0,
    enabled: isOpen
  });

  const handleSubmit = useCallback(
    async (
      isValid: boolean,
      values: FormValuesType,
      reset: () => void
    ): Promise<void> => {
      if (!isValid || checkProjectExists.isLoading) return;
      const exists = await getCheckProjectNameExists(axios, values.projectName);
      if (exists) {
        addSnackbarToQueue({
          message: ERROR_MESSAGE_PROJECT_NAME_EXISTS,
          severity: "error"
        });
      } else {
        const formData = new FormData();
        formData.append("file", file!);

        if (values.enableGrouping) {
          formData.append("project_name", values.projectName);
          formData.append("project_type", values.projectType);
        }

        try {
          await postCreateProject.mutateAsync(formData);
          addSnackbarToQueue({
            message: "New cases were created successfully.",
            severity: "success"
          });
          onClose();
          reset();
        } catch (err) {
          addSnackbarToQueue({
            message: "Error creating cases.",
            severity: "error"
          });
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [addSnackbarToQueue, axios, checkProjectExists.isLoading, postCreateProject]
  );

  useDebounce(() => setDebouncedProjectName(formik.values.projectName), 200, [
    formik.values.projectName
  ]);

  const closeAndResetDialog = (): void => {
    onClose();

    setTimeout(() => {
      formik.setErrors({});
      formik.resetForm();
    }, 50);
  };

  // Reset form when dialog is opened.
  useEffect(() => {
    if (isOpen) {
      formik.setErrors({});
      formik.resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleDropzoneEvent = (event: any): void => {
    if (event[0].type === "application/vnd.ms-excel") {
      if (event[0].name.endsWith(".csv")) {
        setFile(event[0]);
      } else {
        addSnackbarToQueue({
          message: "Please upload a valid CSV file.",
          severity: "error"
        });
      }
    } else {
      setFile(event[0]);
    }
  };

  const handleTemplateDownload = async (
    event: React.MouseEvent<HTMLAnchorElement>
  ): Promise<void> => {
    event.preventDefault();

    await refetchTemplate();

    await downloadFile({
      url: template?.original_file.url ?? "",
      headers: template?.original_file.download_headers,
      signal: controller.signal,
      filename: template?.original_file.name
    });
  };

  const shouldDisableConfirm =
    !file ||
    checkProjectExists.isLoading ||
    checkProjectExists.data ||
    !formik.isValid;

  return (
    <Dialog
      dialogId="cm-create-multiple-cases-dialog"
      title="Create Multiple Cases"
      open={isOpen}
      size="medium"
      fullWidth={true}
      onClose={closeAndResetDialog}
      data-qa="KPI-Create-Multiple-Cases-Dialog"
      confirmDisabled={shouldDisableConfirm}
      confirmText="Save"
      onSubmit={event => {
        event.preventDefault();
        formik.submitForm();
      }}
    >
      <Stack gap={2} alignItems="flex-start">
        <>
          {postCreateProject.isError && (
            <>
              {!!Object.keys(
                postCreateProject.error.response?.data.errors ?? {}
              ).length && (
                <Alert severity="error" sx={{ width: "100%" }}>
                  <Stack gap={1}>
                    {Object.entries(
                      postCreateProject.error.response?.data.errors ?? {}
                    ).map(([fieldName, field]) => (
                      <Box>
                        <Typography
                          component="h2"
                          variant="h6"
                          sx={{ m: 0, fontSize: "1.1rem" }}
                        >
                          {fieldName}
                        </Typography>
                        <ul style={{ margin: "0", paddingLeft: "20px" }}>
                          {field.map(error => {
                            return <li>{error}</li>;
                          })}
                        </ul>
                      </Box>
                    ))}
                  </Stack>
                </Alert>
              )}

              {!!postCreateProject.error.response?.data.count && (
                <>
                  <Alert severity="warning" sx={{ width: "100%" }}>
                    The uploaded CSV had errors, please correct them and
                    re-upload.
                  </Alert>

                  <CsvErrorFeedback
                    errors={postCreateProject.error.response?.data}
                  />
                </>
              )}
            </>
          )}

          <Link href="template.csv" onClick={handleTemplateDownload}>
            Download Template
          </Link>

          <DropZone
            // allow application/vnd.ms-excel for Firefox :/
            acceptedFiles={["text/csv", "application/vnd.ms-excel"]}
            setFileDropped={handleDropzoneEvent}
            dropzoneText="Drag and drop a CSV file here or click to select a file"
            data-qa="CM-Create-Multiple-Cases-Dropzone"
            filesLimit={1}
          />

          {file && (
            <Card sx={{ width: "100%", px: 2, py: 1 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignContent="center"
              >
                <Typography>{file.name}</Typography>

                <IconButton onClick={() => setFile(null)}>
                  <DeleteIcon />
                </IconButton>
              </Stack>
            </Card>
          )}

          {hasFeature("projects") && (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    name="enableGrouping"
                    checked={formik.values.enableGrouping}
                    onChange={formik.handleChange}
                  />
                }
                label="Group Cases into a Project"
              />
              <TextField
                disabled={!formik.values.enableGrouping}
                variant="outlined"
                fullWidth
                name="projectName"
                required
                id="projectName"
                label="Project Name"
                data-qa="CM-Create-Multiple-Cases-TextField-Project-Name"
                inputProps={{ maxLength: NAME_MAX_LENGTH }}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  (formik.touched.projectName &&
                    Boolean(formik.errors.projectName)) ||
                  checkProjectExists.data
                }
                helperText={
                  (formik.touched.projectName && formik.errors.projectName) ||
                  (checkProjectExists.data && ERROR_MESSAGE_PROJECT_NAME_EXISTS)
                }
                value={formik.values.projectName}
              />
              <Dropdown
                disabled={!formik.values.enableGrouping}
                fullWidth
                id="project-type-select"
                name="projectType"
                label="Project Type"
                required
                value={formik.values.projectType}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.projectType &&
                  Boolean(formik.errors.projectType)
                }
                helperText={
                  formik.touched.projectType && formik.errors.projectType
                    ? formik.errors.projectType
                    : undefined
                }
              >
                {ProjectTypes.map((option, index) => (
                  <MenuItem key={index} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Dropdown>
            </>
          )}
        </>
      </Stack>
    </Dialog>
  );
};
